/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';

/** One value the agency already used, as the field-values endpoints answer it. */
export interface FieldSuggestion {
  value: string;
  count: number;
  context: Record<string, string>;
}

/** Fetches the suggestions for one query. */
export type SuggestionFetcher = (query: string) => Promise<FieldSuggestion[]>;

/** Letters typed before the endpoint is asked (plan 2026-10-28, §4). */
export const SUGGEST_MIN_CHARS = 2;
/** Quiet time after the last keystroke before asking. */
export const SUGGEST_DEBOUNCE_MS = 250;

/**
 * Whether a query is long enough to ask for suggestions.
 *
 * @param query - What the person typed.
 * @returns True from two non-blank characters on.
 */
export function isSuggestQuery(query: string): boolean {
  return query.trim().length >= SUGGEST_MIN_CHARS;
}

/**
 * Folds a value the way the backend dedupes it: no accents, no case.
 *
 * @param value - Any text.
 * @returns The folded text.
 */
export function foldValue(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();
}

/**
 * Whether the typed text already is one of the suggestions.
 *
 * @param query - What the person typed.
 * @param suggestions - The values on offer.
 * @returns True when a suggestion matches ignoring accents and case.
 */
export function matchesASuggestion(query: string, suggestions: FieldSuggestion[]): boolean {
  const folded = foldValue(query);
  return suggestions.some((item) => foldValue(item.value) === folded);
}

interface SuggestionsState {
  items: FieldSuggestion[];
  loading: boolean;
}

const EMPTY: SuggestionsState = { items: [], loading: false };

/**
 * Debounced suggestions for a free-text field.
 *
 * Asks only from two characters on, 250 ms after the last keystroke, and
 * drops a response that arrives after the query moved on. A failed request
 * reads as no suggestions: the field stays free text either way.
 *
 * @param query - The current text.
 * @param fetcher - The endpoint call.
 * @param enabled - False while the field is not focused.
 * @returns The suggestions and whether a request is in flight.
 */
export function useFieldSuggestions(
  query: string,
  fetcher: SuggestionFetcher,
  enabled: boolean,
): SuggestionsState {
  const [state, setState] = useState<SuggestionsState>(EMPTY);
  const active = enabled && isSuggestQuery(query);

  useEffect(() => {
    if (!active) {
      setState(EMPTY);
      return undefined;
    }
    let current = true;
    setState((prev) => ({ ...prev, loading: true }));
    const timer = window.setTimeout(() => {
      fetcher(query.trim())
        .then((items) => { if (current) setState({ items, loading: false }); })
        .catch(() => { if (current) setState(EMPTY); });
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [active, query, fetcher]);

  return state;
}
